import React, { useCallback, useState } from 'react';
import {
	Alert, Badge, Button, Card, CardBody, Col,
	FormGroup, Input, Label, Row, Spinner
} from 'reactstrap';
import ReactQuill, { Quill } from 'react-quill';
import imageResize from 'quill-image-resize-module-react';
import handbookService from './HandbookService';
import 'react-quill/dist/quill.snow.css';
import {
	FiPlus, FiTrash2, FiChevronUp, FiChevronDown
} from 'react-icons/fi';

Quill.register('modules/imageResize', imageResize);

const quillModules = {
	toolbar: [
		['bold', 'italic', 'underline'],
		['blockquote'],
		['link', 'image'],
		[{ 'header': 1 }, { 'header': 2 }, { 'header': 3 }],
		[{ 'list': 'ordered' }, { 'list': 'bullet' }],
		[{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
		['clean']
	],
	imageResize: {
		parchment: Quill.import('parchment'),
		modules: ['Resize', 'DisplaySize']
	}
};

const quillFormats = ['header', 'bold', 'italic', 'underline', 'blockquote', 'list', 'bullet', 'link', 'image', 'align'];

const quillDescriptionModules = {
	toolbar: [
		['bold', 'italic', 'underline'],
		['link'],
		[{ 'list': 'ordered' }, { 'list': 'bullet' }],
		['clean']
	]
};

const quillDescriptionFormats = ['bold', 'italic', 'underline', 'link', 'list', 'bullet'];

function ImagePasteUpload({ imageUrl, onUploaded, onUrlChange, label }) {
	const [uploading, setUploading] = useState(false);
	const [err, setErr] = useState('');

	const handlePaste = useCallback(async (e) => {
		const items = e.clipboardData && e.clipboardData.items;
		if (!items) return;
		for (let i = 0; i < items.length; i++) {
			if (items[i].type.indexOf('image') !== -1) {
				e.preventDefault();
				const file = items[i].getAsFile();
				if (!file) return;
				setUploading(true);
				setErr('');
				try {
					const result = await handbookService.uploadFile(file);
					onUploaded(result.url);
				} catch {
					setErr('Upload failed.');
				} finally {
					setUploading(false);
				}
				return;
			}
		}
	}, [onUploaded]);

	const handleFileChange = useCallback(async (e) => {
		const file = e.target.files && e.target.files[0];
		if (!file) return;
		setUploading(true);
		setErr('');
		try {
			const result = await handbookService.uploadFile(file);
			onUploaded(result.url);
		} catch {
			setErr('Upload failed.');
		} finally {
			setUploading(false);
			// clear the file input value if present
			try { if (e && e.target && typeof e.target.value !== 'undefined') e.target.value = ''; } catch (ex) { }
		}
	}, [onUploaded]);

	return (
		<div onPaste={handlePaste}>
			<FormGroup className="mb-1">
				<Label className="hb-admin-label">{label || 'Photo'}</Label>
				{imageUrl && (
					<div className="mb-1">
						<img src={imageUrl} alt="preview" style={{ height: 60, width: 60, objectFit: 'cover', borderRadius: '50%', border: '2px solid #dee2e6' }} />
					</div>
				)}
				<Input
					bsSize="sm"
					value={imageUrl || ''}
					onChange={onUrlChange}
					placeholder="Paste image (Ctrl+V) or enter URL"
					style={{ marginBottom: 4 }}
				/>
				<Input type="file" bsSize="sm" accept="image/*" onChange={handleFileChange} />
				{uploading && <small className="text-info"><Spinner size="sm" /> Uploading...</small>}
				{err && <small className="text-danger">{err}</small>}
			</FormGroup>
		</div>
	);
}

function FilePasteUpload({ fileUrl, onUploaded, onUrlChange, label, accept, placeholder, onUploadFor }) {
	const [uploading, setUploading] = useState(false);
	const [err, setErr] = useState('');

	const handlePaste = useCallback(async (e) => {
		const items = e.clipboardData && e.clipboardData.items;
		if (!items) return;
		for (let i = 0; i < items.length; i++) {
			if (items[i].kind === 'file') {
				e.preventDefault();
				const file = items[i].getAsFile();
				if (!file) return;
				setUploading(true);
				setErr('');
				try {
					const result = await handbookService.uploadFile(file);
					onUploaded(result.url);
					if (onUploadFor) onUploadFor(result.url);
				} catch {
					setErr('Upload failed.');
				} finally {
					setUploading(false);
					// do not attempt to clear e.target here - paste events may not have a target with a value
				}
				return;
			}
		}
	}, [onUploaded, onUploadFor]);

	const handleFileChange = useCallback(async (e) => {
		const file = e.target.files && e.target.files[0];
		if (!file) return;
		setUploading(true);
		setErr('');
		try {
			const result = await handbookService.uploadFile(file);
			onUploaded(result.url);
			if (onUploadFor) onUploadFor(result.url);
		} catch {
			setErr('Upload failed.');
		} finally {
			setUploading(false);
			// clear the file input value if present
			try { if (e && e.target && typeof e.target.value !== 'undefined') e.target.value = ''; } catch (ex) { }
		}
	}, [onUploaded, onUploadFor]);

	return (
		<div onPaste={handlePaste}>
			<FormGroup className="mb-1">
				<Label className="hb-admin-label">{label || 'File'}</Label>
				<Input
					bsSize="sm"
					value={fileUrl || ''}
					onChange={onUrlChange}
					placeholder={placeholder || 'Paste file (Ctrl+V) or enter URL'}
					style={{ marginBottom: 4 }}
				/>
				<Input type="file" bsSize="sm" accept={accept || '*/*'} onChange={handleFileChange} />
				{uploading && <small className="text-info"><Spinner size="sm" /> Uploading...</small>}
				{err && <small className="text-danger">{err}</small>}
			</FormGroup>
		</div>
	);
}

function PersonEntryEditor({ person, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) {
	const update = (field, value) => onChange({ ...person, [field]: value });

	return (
		<Card className="mb-2" style={{ border: '1px solid #dee2e6' }}>
			<CardBody style={{ padding: '12px' }}>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
					<strong style={{ fontSize: '0.85em', color: '#495057' }}>{person.name || 'New Person'}</strong>
					<div style={{ display: 'flex', gap: 4 }}>
						<button className="hb-admin-btn-mini" onClick={onMoveUp} disabled={isFirst} title="Move up"><FiChevronUp size={12} /></button>
						<button className="hb-admin-btn-mini" onClick={onMoveDown} disabled={isLast} title="Move down"><FiChevronDown size={12} /></button>
						<button className="hb-admin-btn-mini hb-admin-btn-delete" onClick={onDelete} title="Remove person"><FiTrash2 size={12} /></button>
					</div>
				</div>
				<Row>
					<Col md={6}>
						<FormGroup className="mb-2">
							<Label className="hb-admin-label">Name</Label>
							<Input bsSize="sm" value={person.name || ''} onChange={e => update('name', e.target.value)} placeholder="Full name" />
						</FormGroup>
					</Col>
					<Col md={6}>
						<FormGroup className="mb-2">
							<Label className="hb-admin-label">Role / Position</Label>
							<Input bsSize="sm" value={person.role || ''} onChange={e => update('role', e.target.value)} placeholder="Job title or role" />
						</FormGroup>
					</Col>
					<Col md={6}>
						<FormGroup className="mb-2">
							<Label className="hb-admin-label">Role (Cymraeg)</Label>
							<Input bsSize="sm" value={person.role_cy || ''} onChange={e => update('role_cy', e.target.value)} placeholder="Welsh role" />
						</FormGroup>
					</Col>
					<Col md={6}>
						<FormGroup className="mb-2">
							<Label className="hb-admin-label">Email</Label>
							<Input bsSize="sm" type="email" value={person.email || ''} onChange={e => update('email', e.target.value)} />
						</FormGroup>
					</Col>
					<Col md={6}>
						<FormGroup className="mb-2">
							<Label className="hb-admin-label">Phone</Label>
							<Input bsSize="sm" value={person.phone || ''} onChange={e => update('phone', e.target.value)} />
						</FormGroup>
					</Col>
					<Col md={12}>
						<FormGroup className="mb-2">
							<Label className="hb-admin-label">Address</Label>
							<Input bsSize="sm" type="textarea" rows={3} value={person.address || ''} onChange={e => update('address', e.target.value)} placeholder="Optional address" />
						</FormGroup>
					</Col>
					<Col md={12}>
						<ImagePasteUpload
							label="Photo (paste from clipboard, upload, or enter URL)"
							imageUrl={person.imageUrl}
							onUploaded={url => update('imageUrl', url)}
							onUrlChange={e => update('imageUrl', e.target.value)}
						/>
					</Col>
				</Row>
			</CardBody>
		</Card>
	);
}

export function ItemEditorPanel({
	item, section,
	onItemChange, onUpdateItem,
	onAddPerson, onDeletePerson, onMovePerson, onUpdatePerson,
	onAddLink, onDeleteLink, onMoveLink, onUpdateLink,
	onAddPdf, onDeletePdf, onMovePdf, onUpdatePdf,
	uploading, uploadPdfFileFor
}) {
	if (!item) return null;

	const selectedType = item.itemType || 'TextBlock';
	const showLink = selectedType === 'Link';
	const showPdf = selectedType === 'Pdf';
	const showPersonCard = selectedType === 'PersonCard';
	const showRichContent = !showPersonCard && !showLink && !showPdf;

	const handleItemChange = (event) => {
		const { name, value, type, checked } = event.target;
		const nextValue = type === 'checkbox' ? checked : value;
		onItemChange(name, nextValue);
	};

	return (
		<div className="hb-admin-item-editor-inner">
			<div className="hb-admin-item-editor-header">
				<h6 className="mb-0">Editing: {item.title || item.title_cy || (item.people && item.people.length > 0 ? item.people[0].name : '') || '(no title)'}</h6>
				<Badge color={item.enabled ? 'success' : 'secondary'}>{item.enabled ? 'Enabled' : 'Disabled'}</Badge>
			</div>

			<div className="hb-admin-item-fields">
				<Row>
					<Col md={8}>
						<FormGroup>
							<Label className="hb-admin-label">Title <span className="text-muted font-weight-normal">(optional � used as subsection heading in contents)</span></Label>
							<Input name="title" value={item.title || ''} onChange={handleItemChange} placeholder="Leave blank for no heading" />
						</FormGroup>
					</Col>
					<Col md={4}>
						<FormGroup>
							<Label className="hb-admin-label">Type</Label>
							<Input type="select" bsSize="sm" name="itemType" value={item.itemType || 'TextBlock'} onChange={handleItemChange}>
								<option value="TextBlock">Text Block</option>
								<option value="Link">Link</option>
								<option value="Pdf">PDF</option>
								<option value="PersonCard">Person Card</option>
							</Input>
						</FormGroup>
					</Col>
				</Row>

				<Row>
					<Col md={4}>
						<FormGroup>
							<Label className="hb-admin-label">Order</Label>
							<Input type="number" bsSize="sm" name="displayOrder" value={item.displayOrder || 0} onChange={handleItemChange} />
						</FormGroup>
					</Col>
					<Col md={4}>
						<FormGroup>
							<Label className="hb-admin-label">Active</Label>
							<div>
								<Button
									size="sm"
									color={item.enabled ? 'success' : 'secondary'}
									outline={!item.enabled}
									onClick={() => onItemChange('enabled', !item.enabled)}
								>
									{item.enabled ? 'On' : 'Off'}
								</Button>
							</div>
						</FormGroup>
					</Col>
					<Col md={4}>
						<FormGroup>
							<Label className="hb-admin-label">Collapsible</Label>
							<div>
								<Button
									size="sm"
									color={item.isCollapsible ? 'info' : 'secondary'}
									outline={!item.isCollapsible}
									onClick={() => onItemChange('isCollapsible', !item.isCollapsible)}
								>
									{item.isCollapsible ? 'On' : 'Off'}
								</Button>
							</div>
						</FormGroup>
					</Col>
				</Row>

				{/* Welsh title */}
				<Row>
					<Col md={12}>
						<FormGroup>
							<Label className="hb-admin-label">Title (Cymraeg)</Label>
							<Input name="title_cy" value={item.title_cy || ''} onChange={handleItemChange} placeholder="Optional Welsh title" />
						</FormGroup>
					</Col>
				</Row>

				{!showPersonCard && showRichContent && (
					<FormGroup>
						<Label className="hb-admin-label">Content</Label>
						<ReactQuill
							key={`item-content-${item.id || 'new'}`}
							value={item.content || ''}
							onChange={(value) => onItemChange('content', value)}
							modules={quillModules}
							formats={quillFormats}
						/>
						{/* Welsh content editor */}
						<Label className="hb-admin-label mt-2">Content (Cymraeg)</Label>
						<ReactQuill
							key={`item-content-cy-${item.id || 'new'}`}
							value={item.content_cy || ''}
							onChange={(value) => onItemChange('content_cy', value)}
							modules={quillModules}
							formats={quillFormats}
						/>
					</FormGroup>
				)}

				{showLink && (
					<div className="mt-2">
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
							<Label className="hb-admin-label mb-0">Links <span className="text-muted font-weight-normal">({(item.links || []).length})</span></Label>
							<Button color="success" size="sm" onClick={onAddLink}><FiPlus size={12} style={{ marginRight: '3px' }} /> Add Link</Button>
						</div>
						{(item.links || []).length === 0 && <p className="text-muted" style={{ fontSize: '0.9em' }}>No links added yet.</p>}
						{(item.links || []).map((link, idx) => (
							<Card key={link.id || idx} className="mb-2" style={{ border: '1px solid #dee2e6' }}>
								<CardBody style={{ padding: '12px' }}>
									<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
										<strong style={{ fontSize: '0.85em', color: '#495057' }}>{link.name || link.name_cy || 'New Link'}</strong>
										<div style={{ display: 'flex', gap: 4 }}>
											<button className="hb-admin-btn-mini" onClick={() => onMoveLink(link.id, -1)} disabled={idx === 0} title="Move up"><FiChevronUp size={12} /></button>
											<button className="hb-admin-btn-mini" onClick={() => onMoveLink(link.id, 1)} disabled={idx === (item.links || []).length - 1} title="Move down"><FiChevronDown size={12} /></button>
											<button className="hb-admin-btn-mini hb-admin-btn-delete" onClick={() => onDeleteLink(link.id)} title="Remove link"><FiTrash2 size={12} /></button>
										</div>
									</div>
									<FormGroup className="mb-2"><Label className="hb-admin-label">Name</Label><Input bsSize="sm" value={link.name || ''} onChange={e => {
											const value = e.target.value;
											onUpdateLink(link.id, x => ({ ...x, name: value }));
										}} placeholder="Link title" /></FormGroup>
									<FormGroup className="mb-2"><Label className="hb-admin-label">Name (Cymraeg)</Label><Input bsSize="sm" value={link.name_cy || ''} onChange={e => {
											const value = e.target.value;
											onUpdateLink(link.id, x => ({ ...x, name_cy: value }));
										}} placeholder="Welsh title" /></FormGroup>
									<FormGroup className="mb-2"><Label className="hb-admin-label">URL</Label><Input bsSize="sm" value={link.url || ''} onChange={e => {
											const value = e.target.value;
											onUpdateLink(link.id, x => ({ ...x, url: value }));
										}} placeholder="https://..." /></FormGroup>
									<FormGroup className="mb-2"><Label className="hb-admin-label">Alternate URL (Cymraeg)</Label><Input bsSize="sm" value={link.url_cy || ''} onChange={e => {
											const value = e.target.value;
											onUpdateLink(link.id, x => ({ ...x, url_cy: value }));
										}} placeholder="https://... (Welsh)" /></FormGroup>
									<ImagePasteUpload
										label="Logo Image (paste from clipboard, upload, or enter URL)"
										imageUrl={link.logoImageUrl}
										onUploaded={url => onUpdateLink(link.id, x => ({ ...x, logoImageUrl: url }))
										}
										onUrlChange={e => {
											const value = e.target.value;
											onUpdateLink(link.id, x => ({ ...x, logoImageUrl: value }));
										}}
									/>
									<FormGroup className="mb-0">
										<Label className="hb-admin-label">Description</Label>
										<ReactQuill
											value={link.htmlContent || ''}
											onChange={(value) => onUpdateLink(link.id, x => ({ ...x, htmlContent: value }))
											}
											modules={quillDescriptionModules}
											formats={quillDescriptionFormats}
										/>
										{/* Welsh description */}
										<Label className="hb-admin-label mt-2">Description (Cymraeg)</Label>
										<ReactQuill
											value={link.htmlContent_cy || ''}
											onChange={(value) => onUpdateLink(link.id, x => ({ ...x, htmlContent_cy: value }))
											}
											modules={quillDescriptionModules}
											formats={quillDescriptionFormats}
										/>
									</FormGroup>
								</CardBody>
							</Card>
						))}
					</div>
				)}

				{showPdf && (
					<div className="mt-2">
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
							<Label className="hb-admin-label mb-0">PDFs <span className="text-muted font-weight-normal">({(item.pdfs || []).length})</span></Label>
							<Button color="success" size="sm" onClick={onAddPdf}><FiPlus size={12} style={{ marginRight: '3px' }} /> Add PDF</Button>
						</div>
						{(item.pdfs || []).length === 0 && <p className="text-muted" style={{ fontSize: '0.9em' }}>No PDFs added yet.</p>}
						{(item.pdfs || []).map((pdf, idx) => (
							<Card key={pdf.id || idx} className="mb-2" style={{ border: '1px solid #dee2e6' }}>
								<CardBody style={{ padding: '12px' }}>
									<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
										<strong style={{ fontSize: '0.85em', color: '#495057' }}>{pdf.name || pdf.name_cy || 'New PDF'}</strong>
										<div style={{ display: 'flex', gap: 4 }}>
											<button className="hb-admin-btn-mini" onClick={() => onMovePdf(pdf.id, -1)} disabled={idx === 0} title="Move up"><FiChevronUp size={12} /></button>
											<button className="hb-admin-btn-mini" onClick={() => onMovePdf(pdf.id, 1)} disabled={idx === (item.pdfs || []).length - 1} title="Move down"><FiChevronDown size={12} /></button>
											<button className="hb-admin-btn-mini hb-admin-btn-delete" onClick={() => onDeletePdf(pdf.id)} title="Remove PDF"><FiTrash2 size={12} /></button>
										</div>
									</div>
									<FormGroup className="mb-2"><Label className="hb-admin-label">Name</Label><Input bsSize="sm" value={pdf.name || ''} onChange={e => {
										const value = e.target.value;
										onUpdatePdf(pdf.id, x => ({ ...x, name: value }));
									}} placeholder="Document title" /></FormGroup>
									<FormGroup className="mb-2"><Label className="hb-admin-label">Name (Cymraeg)</Label><Input bsSize="sm" value={pdf.name_cy || ''} onChange={e => {
										const value = e.target.value;
										onUpdatePdf(pdf.id, x => ({ ...x, name_cy: value }));
									}} placeholder="Welsh title" /></FormGroup>
									<FilePasteUpload
										label="PDF File (paste from clipboard, upload, or enter URL)"
										fileUrl={pdf.url}
										accept=".pdf,application/pdf"
										placeholder="Paste file (Ctrl+V) or enter URL"
										onUploaded={url => onUpdatePdf(pdf.id, x => ({ ...x, url: url }))
										}
										onUrlChange={e => {
											const value = e.target.value;
											onUpdatePdf(pdf.id, x => ({ ...x, url: value }));
										}}
									/>

									{/* Welsh PDF file upload control */}
									<FilePasteUpload
										label="PDF File (Cymraeg) (paste from clipboard, upload, or enter URL)"
										fileUrl={pdf.url_cy}
										accept=".pdf,application/pdf"
										placeholder="Paste Welsh file (Ctrl+V) or enter URL"
										onUploaded={url => onUpdatePdf(pdf.id, x => ({ ...x, url_cy: url }))
										}
										onUrlChange={e => {
											const value = e.target.value;
											onUpdatePdf(pdf.id, x => ({ ...x, url_cy: value }));
										}}
										onUploadFor={url => onUpdatePdf(pdf.id, x => ({ ...x, url_cy: url }))
										}
									/>
									<FormGroup className="mb-0">
										<Label className="hb-admin-label">Description</Label>
										<ReactQuill
											value={pdf.htmlContent || ''}
											onChange={(value) => onUpdatePdf(pdf.id, x => ({ ...x, htmlContent: value }))
											}
											modules={quillDescriptionModules}
											formats={quillDescriptionFormats}
										/>
										{/* Welsh description */}
										<Label className="hb-admin-label mt-2">Description (Cymraeg)</Label>
										<ReactQuill
											value={pdf.htmlContent_cy || ''}
											onChange={(value) => onUpdatePdf(pdf.id, x => ({ ...x, htmlContent_cy: value }))
											}
											modules={quillDescriptionModules}
											formats={quillDescriptionFormats}
										/>
									</FormGroup>
								</CardBody>
							</Card>
						))}
					</div>
				)}

				{showPersonCard && (
					<div className="mt-2">
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
							<Label className="hb-admin-label mb-0">
								People <span className="text-muted font-weight-normal">({(item.people || []).length})</span>
							</Label>
							<Button color="success" size="sm" onClick={onAddPerson}>
								<FiPlus size={12} style={{ marginRight: '3px' }} /> Add Person
							</Button>
						</div>
						{(item.people || []).length === 0 && (
							<p className="text-muted" style={{ fontSize: '0.9em' }}>No people added yet. Click <strong>Add Person</strong> to start.</p>
						)}
						{(item.people || []).map((person, idx) => (
							<PersonEntryEditor
								key={person.id}
								person={person}
								isFirst={idx === 0}
								isLast={idx === (item.people || []).length - 1}
								onChange={(updated) => onUpdatePerson(person.id, () => updated)}
								onDelete={() => onDeletePerson(person.id)}
								onMoveUp={() => onMovePerson(person.id, -1)}
								onMoveDown={() => onMovePerson(person.id, 1)}
							/>
						))}
					</div>
				)}

				{uploading && <Alert color="info" className="py-2 mt-2"><Spinner size="sm" /> Uploading file...</Alert>}
			</div>
		</div>
	);
}
